import React, {useEffect, useState} from 'react';
import {View, TouchableOpacity} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import FontAwesome5 from 'react-native-vector-icons/FontAwesome5';
import Ionicons from 'react-native-vector-icons/Ionicons';

import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import {useToast} from '@/toast/useToast';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

import useGetMyReferralCodeApi from '@/Hooks/ReferralApi/useGetMyReferralCodeApi';
import useGetMyReferralsApi, {
  ReferralRow,
} from '@/Hooks/ReferralApi/useGetMyReferralsApi';

import {styles} from './styles';

// Kept in sync with chatbot's REFERRAL_LEADERBOARD_TOP_N /
// REFERRAL_LEADERBOARD_MIN_CONVERSIONS (auth_service/constants.py) -- the only
// referral reward mechanic that's actually decided/live for v1. See
// docs/referral-program-plan.md (chess repo).
const LEADERBOARD_TOP_N = 50;
const LEADERBOARD_MIN_CONVERSIONS = 20;

const copyToClipboard = (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(text);
  }
};

// "Invited" is intentionally never rendered -- see docs/referral-program-plan.md
// (chess repo): the backend only ever produces signed_up/converted/rewarded rows,
// since there's no way to track a pre-signup invite without a recipient identity.
const STATUS_LABELS: Record<string, string> = {
  signed_up: 'Signed Up',
  converted: 'Converted',
  rewarded: 'Converted',
};

const STATUS_STYLES: Record<string, {bg: string; color: string}> = {
  signed_up: {bg: '#FEF2B5', color: CCColors.Primary.Dark},
  converted: {bg: '#E4F6EB', color: '#2F8F57'},
  rewarded: {bg: '#E4F6EB', color: '#2F8F57'},
};

const initialsFor = (name?: string | null) => {
  if (!name) return '#';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
};

const ReferAFriend: React.FC = () => {
  const {isMobile, isMobileOrTab} = useMediaQuery();
  const showToast = useToast();
  const [codeCopied, setCodeCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [page, setPage] = useState(1);

  const {mutate: fetchMyCode, data: myCode} = useGetMyReferralCodeApi();
  const {mutate: fetchMyReferrals, data: myReferrals} = useGetMyReferralsApi();

  useEffect(() => {
    fetchMyCode();
  }, []);

  useEffect(() => {
    fetchMyReferrals({page});
  }, [page]);

  const handleCopyCode = () => {
    if (!myCode?.code) return;
    copyToClipboard(myCode.code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1600);
  };

  const handleCopyLink = () => {
    if (!myCode?.share_link) return;
    copyToClipboard(myCode.share_link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 1600);
  };

  const handleShareInvite = async () => {
    if (!myCode?.share_link) return;
    if (typeof navigator !== 'undefined' && (navigator as any).share) {
      try {
        await (navigator as any).share({
          text: myCode.share_message,
          url: myCode.share_link,
        });
        return;
      } catch (e) {
        // user cancelled or the native share sheet failed -- fall through to copy
      }
    }
    // No Web Share API here (most desktop browsers) -- copy the link instead of
    // silently forcing WhatsApp, which is its own explicit button below.
    copyToClipboard(myCode.share_link);
    showToast('Invite link copied! Paste it anywhere to share.');
  };

  const handleShareWhatsApp = () => {
    if (!myCode?.share_message) return;
    const url = `https://wa.me/?text=${encodeURIComponent(myCode.share_message)}`;
    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    }
  };

  const referrals: ReferralRow[] = myReferrals?.referrals || [];
  const totalPages = myReferrals
    ? Math.max(1, Math.ceil(myReferrals.total / myReferrals.page_size))
    : 1;

  return (
    <View style={styles.wrapper}>
      <View style={[styles.banner, isMobileOrTab && styles.bannerMobile]}>
        <View style={{flex: 1}}>
          <CCText
            color={CCColors.White}
            fontSize={isMobile ? 18 : 24}
            style={styles.bannerTitle}>
            Invite friends, earn rewards together
          </CCText>
          <CCText
            color={CCColors.White}
            fontSize={isMobile ? 13 : 14}
            style={styles.bannerBody}>
            Share CircleChess with a friend. When they join and start playing, you
            both move closer to your next reward.
          </CCText>
        </View>
        <View
          style={[
            styles.bannerIconWrap,
            isMobileOrTab && styles.bannerIconWrapMobile,
          ]}>
          <Ionicons
            name="people"
            size={isMobile ? 18 : 24}
            color={CCColors.White}
          />
        </View>
      </View>

      <View style={styles.rewardInfoRow}>
        <Feather name="award" size={16} color={CCColors.Primary.Dark} />
        <CCText style={styles.rewardInfoText}>
          Top {LEADERBOARD_TOP_N} referrers each month (min.{' '}
          {LEADERBOARD_MIN_CONVERSIONS} completed referrals) win a CircleChess
          T-shirt.
        </CCText>
      </View>

      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.cardHeading}>
          <View style={styles.cardHeadingIcon}>
            <Feather name="link-2" size={18} color={CCColors.Primary.Dark} />
          </View>
          <CCText style={styles.cardHeadingText}>Your Referral Code</CCText>
        </View>
        <CCText style={styles.helperText}>
          Share this with friends and family. Once they sign up and play 3
          games, the referral counts towards the month's leaderboard.
        </CCText>

        <View style={styles.codeRow}>
          <TouchableOpacity style={styles.codeChip} onPress={handleCopyCode}>
            <CCText style={styles.codeText}>{myCode?.code || '••••••••'}</CCText>
            <CCText style={[styles.copyHint, codeCopied && styles.copyHintActive]}>
              {codeCopied ? 'Copied!' : 'Tap to copy'}
            </CCText>
          </TouchableOpacity>

          <TouchableOpacity style={styles.linkPill} onPress={handleCopyLink}>
            <CCText style={styles.linkText} showEllipses>
              {linkCopied ? 'Copied!' : myCode?.share_link || ''}
            </CCText>
          </TouchableOpacity>

          <CCButton
            type="fill"
            style={[styles.shareButton, isMobile && styles.shareButtonMobile]}
            titleStyle={styles.shareButtonText}
            onPress={handleShareInvite}>
            Share Invite
          </CCButton>

          <TouchableOpacity
            style={[styles.iconButton, styles.whatsappButton]}
            onPress={handleShareWhatsApp}>
            <FontAwesome5 name="whatsapp" size={18} color={CCColors.White} />
          </TouchableOpacity>

          <TouchableOpacity style={styles.iconButton} onPress={handleCopyLink}>
            {linkCopied ? (
              <Feather name="check" size={18} color={CCColors.Green} />
            ) : (
              <Feather name="copy" size={18} color={CCColors.Primary.Dark} />
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.iconButton} onPress={handleShareInvite}>
            <Feather name="share-2" size={18} color={CCColors.Primary.Dark} />
          </TouchableOpacity>
        </View>
      </View>

      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.cardHeading}>
          <View style={styles.cardHeadingIcon}>
            <Feather name="users" size={18} color={CCColors.Primary.Dark} />
          </View>
          <CCText style={styles.cardHeadingText}>Your Referrals</CCText>
        </View>

        {referrals.length === 0 ? (
          <View style={styles.emptyState}>
            <View style={styles.emptyIconWrap}>
              <Feather name="user-plus" size={22} color={CCColors.Primary.Dark} />
            </View>
            <CCText fontWeight="700" fontSize={16}>
              No referrals yet
            </CCText>
            <CCText style={styles.helperText}>
              Share your code above and friends you invite will show up here as
              they join.
            </CCText>
            <CCButton
              type="fill"
              style={styles.shareButton}
              titleStyle={styles.shareButtonText}
              onPress={handleShareInvite}>
              Share Invite
            </CCButton>
          </View>
        ) : (
          <>
            {referrals.map((referral, idx) => {
              const statusStyle =
                STATUS_STYLES[referral.status] || STATUS_STYLES.signed_up;
              const pct = referral.games_required
                ? Math.min(
                    100,
                    Math.round(
                      (referral.games_played / referral.games_required) * 100,
                    ),
                  )
                : 0;
              const nameBlock = (
                <View style={styles.rowNameCol}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}>
                    <CCText style={styles.rowName}>
                      {referral.name || referral.masked_phone || 'Referral'}
                    </CCText>
                    <View
                      style={[
                        styles.statusPill,
                        {backgroundColor: statusStyle.bg},
                      ]}>
                      <CCText
                        style={[
                          styles.statusPillText,
                          {color: statusStyle.color},
                        ]}>
                        {STATUS_LABELS[referral.status] || referral.status}
                      </CCText>
                    </View>
                    {referral.status === 'rewarded' && (
                      <View style={styles.rewardedBadge}>
                        <CCText fontSize={11} fontWeight="700">
                          ★ Rewarded
                        </CCText>
                      </View>
                    )}
                  </View>
                </View>
              );
              const progressBlock = (
                <View>
                  <View
                    style={[
                      styles.progressTrack,
                      isMobileOrTab && styles.progressTrackMobile,
                    ]}>
                    <View
                      style={[styles.progressFill, {width: `${pct}%`}]}
                    />
                  </View>
                  <CCText style={styles.progressLabel}>
                    {referral.games_played}/{referral.games_required} games
                    played
                  </CCText>
                </View>
              );

              if (isMobileOrTab) {
                return (
                  <View style={[styles.row, styles.rowMobile]} key={idx}>
                    <View style={styles.rowTopMobile}>
                      <View style={styles.avatar}>
                        <CCText style={styles.avatarText}>
                          {referral.name ? initialsFor(referral.name) : '#'}
                        </CCText>
                      </View>
                      {nameBlock}
                    </View>
                    <View style={styles.rowBottomMobile}>
                      {progressBlock}
                      <CCText style={styles.dateText}>
                        {referral.joined_at}
                      </CCText>
                    </View>
                  </View>
                );
              }

              return (
                <View style={styles.row} key={idx}>
                  <View style={styles.avatar}>
                    <CCText style={styles.avatarText}>
                      {referral.name ? initialsFor(referral.name) : '#'}
                    </CCText>
                  </View>
                  {nameBlock}
                  {progressBlock}
                  <CCText style={styles.dateText}>{referral.joined_at}</CCText>
                </View>
              );
            })}

            {totalPages > 1 && (
              <View style={styles.pagerRow}>
                <TouchableOpacity
                  disabled={page <= 1}
                  style={[styles.pagerButton, page <= 1 && {opacity: 0.4}]}
                  onPress={() => setPage(p => Math.max(1, p - 1))}>
                  <CCText>Prev</CCText>
                </TouchableOpacity>
                <CCText style={styles.pagerLabel}>
                  Page {page} of {totalPages}
                </CCText>
                <TouchableOpacity
                  disabled={page >= totalPages}
                  style={[
                    styles.pagerButton,
                    page >= totalPages && {opacity: 0.4},
                  ]}
                  onPress={() => setPage(p => Math.min(totalPages, p + 1))}>
                  <CCText>Next</CCText>
                </TouchableOpacity>
              </View>
            )}
          </>
        )}
      </View>
    </View>
  );
};

export default ReferAFriend;
